import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@time-tracker/shared";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * Auth route shell.
 *
 * ChronosTracker no longer wraps auth in the shared `AuthLayout` (split / hero /
 * aside / focus + glass `AuthCard`) that every clone inherited. Each auth screen
 * now renders its OWN `AuthScene` (the stopwatch-stage shell, see
 * `components/auth/auth-scene.tsx`), which owns the page background, scroll
 * container and brand ribbon. So this route is just the transition boundary
 * around the active screen — no per-screen layout config here anymore.
 */
function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login to ADD another account while the
    // current one stays signed in (Gmail style). The "already authenticated →
    // go home" gate must be skipped here, otherwise the switcher's "Add account"
    // would bounce straight back to the app and you could never add a 2nd login.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    //
    // P23/CC-60 · I1-bis: this `await` CANNOT be unlimited. A STALLED
    // `get-session` (held — neither 401 nor 5xx, which resolve quickly and
    // therefore made phase-1 probes green) left this `beforeLoad` unresolved,
    // the router unable to mount the route, and `/login` with
    // `document.body.innerText` EMPTY at 3/6/10/15/20 s, including on the
    // production bundle. The sibling `_app` guard already had a ceiling; this
    // one lost it because the number was a local literal there — hence the
    // shared constant.
    //
    // The request is NOT canceled: it stays alive and syncs machine + cache when
    // it arrives. If a session exists, `BootManager` takes the user off the
    // login screen; at most, there is a flicker, never an empty screen.
    const fetchSession = async () =>
      api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
    type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

    const syncReply = (reply: FetchResult) => {
      const { data: replyData, error: replyError } = reply;
      // Synchronize the machine and the cache immediately.
      // This prevents the BootManager from seeing a "pending" or stale session
      // during the route transition, which causes redirection loops.
      sessionActor.send({
        type: "SERVER_REPLY",
        session: isValidSession(replyData) ? cloneSession(replyData) : null,
        error: replyError ?? null,
      });
      queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
      return reply;
    };

    const inFlight = fetchSession()
      .then(syncReply)
      // Network error — let user try to log in anyway.
      .catch(() => null);

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      // Timeout or network error: the login is RENDERED. The late response remains
      // attached above, and BootManager redirects if it contains a session.
      return;
    }

    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    // Decide from the session the machine accepted, not from the raw reply: a
    // stale get-session that LOGOUT revoked must not bounce the guest off /login.
    if (sessionActor.getSnapshot().context.session) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, or null session — not authenticated, show login page.
  },
  component: AuthLayoutRoute,
});
